import React, { useState } from "react";
import {
  Box,
  Text,
  Button,
  Image,
  List,
  ListItem,
  HStack,
  IconButton,
  CircularProgress,
  CircularProgressLabel,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import uploadicon from "../../../assets/upload.png";
import pdfIcon from "../../../assets/waveflowmain/filesIcon/pdf.png";
import docIcon from "../../../assets/waveflowmain/filesIcon/doc.png";
import jsIcon from "../../../assets/waveflowmain/filesIcon/js.png";
import pyIcon from "../../../assets/waveflowmain/filesIcon/py.png";
import jsonIcon from "../../../assets/waveflowmain/filesIcon/json.png";
import txtIcon from "../../../assets/waveflowmain/filesIcon/txt.png";
import csvIcon from "../../../assets/waveflowmain/filesIcon/csv.png";
import { commonStyle } from "../../../styles/commonStyle";
import apiClient from "../../../allApi/interceptorsApiCall";
import { useCustomTost } from "../../../utiils/useCustomTost";

const FileUpload1 = ({ formData, setFormData, files, setFiles }) => {
  const toast = useCustomTost();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgressMap, setUploadProgressMap] = useState({});

  const getFileIcon = (fileName) => {
    if (!fileName) return pdfIcon;
    const extension = fileName.split(".").pop().toLowerCase();
    const icons = {
      pdf: pdfIcon,
      doc: docIcon,
      py: pyIcon,
      js: jsIcon,
      txt: txtIcon,
      csv: csvIcon,
      json: jsonIcon,
    };
    return icons[extension] || pdfIcon;
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await apiClient.post("/file", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgressMap((prev) => ({
            ...prev,
            [file.name]: percentCompleted,
          }));
        },
      });

      if (response?.data?.status === "uploaded") {
        setUploadedFiles((prev) => [...prev, response.data.filename]);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error uploading file", error);
      toast(
        "Error",
        error?.response?.data?.error || `Failed to upload ${file.name}`,
        "error"
      );
      return false;
    } finally {
      setUploadProgressMap((prev) => {
        const updated = { ...prev };
        delete updated[file.name];
        return updated;
      });
    }
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);

    if (!selectedFiles || selectedFiles.length === 0) return;

    const remainingSlots = 10 - files.length;

    if (remainingSlots <= 0) {
      toast("Warning", "Maximum 10 files allowed", "warning");
      return;
    }

    const filesToProcess = selectedFiles.slice(0, remainingSlots);

    if (selectedFiles.length > remainingSlots) {
      toast(
        "Warning",
        `Only ${remainingSlots} more files can be added`,
        "warning"
      );
    }

    setFiles((prev) => [...prev, ...filesToProcess]);
    setIsLoading(true);

    try {
      await Promise.all(
        filesToProcess.map(async (file) => {
          const success = await uploadFile(file);
          if (!success) {
            setFiles((prev) => prev.filter((f) => f.name !== file.name));
          }
        })
      );
    } catch (error) {
      toast("Error", "Failed to process some files", "error");
    } finally {
      setIsLoading(false);
      e.target.value = "";
    }
  };

  // const handleFileChange = async (e) => {
  //   const selectedFiles = Array.from(e.target.files);

  //   if (!selectedFiles || selectedFiles.length === 0) return;

  //   const remainingSlots = 10 - files.length;

  //   if (remainingSlots <= 0) {
  //     toast("Warning", "Maximum 10 files allowed", "warning");
  //     return;
  //   }

  //   const filesToProcess = selectedFiles.slice(0, remainingSlots);

  //   if (selectedFiles.length > remainingSlots) {
  //     toast(
  //       "Warning",
  //       `Only ${remainingSlots} more files can be added`,
  //       "warning"
  //     );
  //   }

  //   setFiles((prev) => [...prev, ...filesToProcess]);

  //   setIsLoading(true);

  //   try {
  //     for (const file of filesToProcess) {
  //       const success = await uploadFile(file);
  //       if (!success) {
  //         setFiles((prev) => prev.filter((f) => f.name !== file.name));
  //       }
  //     }
  //   } catch (error) {
  //     toast("Error", "Failed to process uploads", "error");
  //   } finally {
  //     setIsLoading(false);
  //     e.target.value = "";
  //   }
  // };

  const trunwords = (text, wordLength) => {
    if (text?.length > wordLength) {
      return text.slice(0, wordLength) + "...";
    }
    return text;
  };

  return (
    <Box
      borderWidth={2}
      borderStyle="dashed"
      h={"100%"}
      borderColor="gray.300"
      p={2}
      w={"100%"}
      borderRadius="md"
      display="flex"
      alignItems="center"
    >
      <input
        type="file"
        onChange={handleFileChange}
        style={{ display: "none" }}
        id="fileInput"
        multiple
      />
      <Button
        size={"md"}
        onClick={() => document.getElementById("fileInput").click()}
      >
        <Image src={uploadicon} />
      </Button>
      <HStack w={"90%"}>
        {files.length > 0 && (
          <List
            display="flex"
            gap={4}
            overflowY="auto"
            sx={{ ...commonStyle.sx, scrollBehavior: "smooth" }}
            p={2}
          >
            {files.map((file, index) => (
              <ListItem
                key={index}
                position="relative"
                display="flex"
                flexDirection="column"
                alignItems="center"
                bg="gray.100"
                p={1}
                borderRadius="md"
                boxShadow="md"
                minW="80px"
              >
                <IconButton
                  icon={<CloseIcon boxSize="12px" p={1} />}
                  size="xxs"
                  aria-label="Delete file"
                  position="absolute"
                  top="-3px"
                  right="-3px"
                  bg="black"
                  color="white"
                  _hover={{ bg: "gray.800" }}
                  onClick={() => setFiles(files.filter((_, i) => i !== index))}
                />

                {uploadProgressMap[file.name] > 0 &&
                  uploadProgressMap[file.name] < 100 && (
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      zIndex={2}
                    >
                      <CircularProgress
                        value={uploadProgressMap[file.name]}
                        size="40px"
                        color="purple.500"
                      >
                        <CircularProgressLabel fontSize="xs">
                          {uploadProgressMap[file.name]}%
                        </CircularProgressLabel>
                      </CircularProgress>
                    </Box>
                  )}

                <Box
                  display="flex"
                  flexDirection={"column"}
                  alignItems="center"
                  opacity={
                    uploadProgressMap[file.name] > 0 &&
                    uploadProgressMap[file.name] < 100
                      ? 0.3
                      : 1
                  }
                >
                  <Image
                    src={getFileIcon(file.name)}
                    alt="File Icon"
                    boxSize="14px"
                  />
                  <Text fontSize={"10px"}>{trunwords(file?.name, 6)}</Text>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </HStack>
    </Box>
  );
};

export default FileUpload1;
